type OidcRuntimeEnv = {
  initiateUrl: string;
  idpBaseUrl: string;
  clientId: string;
  redirectUri: string;
  scope: string;
  postLoginPath: string;
  buttonLabel: string;
};

const getEnv = (key: string): string => {
  const viteEnvValue = import.meta.env[key as keyof ImportMetaEnv];
  if (typeof viteEnvValue === 'string' && viteEnvValue.length > 0) {
    return viteEnvValue;
  }

  const processEnvValue = process.env[key];
  if (typeof processEnvValue === 'string' && processEnvValue.length > 0) {
    return processEnvValue;
  }

  return '';
};

const normalizePath = (value: string, fallback: string) => {
  if (!value) {
    return fallback;
  }

  return value.startsWith('/') ? value : `/${value}`;
};

export const getOidcRuntimeEnv = (blocksBaseUrl?: string): OidcRuntimeEnv => {
  const idpBaseUrl =
    getEnv('VITE_OIDC_IDP_BASE_URL') ||
    getEnv('REACT_APP_PUBLIC_OIDC_IDP_BASE_URL') ||
    getEnv('VITE_OIDC_INITIATE_BASE_URL') ||
    getEnv('REACT_APP_PUBLIC_OIDC_INITIATE_BASE_URL') ||
    (blocksBaseUrl ? `https://${blocksBaseUrl}` : '');
  const initiateBaseUrl =
    getEnv('VITE_OIDC_INITIATE_BASE_URL') ||
    getEnv('REACT_APP_PUBLIC_OIDC_INITIATE_BASE_URL') ||
    idpBaseUrl;
  const initiateUrl =
    getEnv('VITE_OIDC_INITIATE_URL') ||
    getEnv('REACT_APP_PUBLIC_OIDC_INITIATE_URL') ||
    (initiateBaseUrl ? `${initiateBaseUrl.replace(/\/$/, '')}/idp/initiate` : '');

  const redirectUri =
    getEnv('VITE_OIDC_REDIRECT_URI') ||
    getEnv('REACT_APP_PUBLIC_OIDC_REDIRECT_URI') ||
    `${window.location.origin}${normalizePath(getEnv('VITE_OIDC_CALLBACK_PATH'), '/login/callback')}`;

  return {
    initiateUrl,
    idpBaseUrl,
    clientId: getEnv('VITE_OIDC_CLIENT_ID') || getEnv('REACT_APP_PUBLIC_OIDC_CLIENT_ID'),
    redirectUri,
    scope: getEnv('VITE_OIDC_SCOPE') || getEnv('REACT_APP_PUBLIC_OIDC_SCOPE') || 'openid',
    postLoginPath: normalizePath(
      getEnv('VITE_POST_LOGIN_PATH') || getEnv('REACT_APP_PUBLIC_POST_LOGIN_PATH'),
      '/chat'
    ),
    buttonLabel:
      getEnv('VITE_OIDC_LOGIN_BUTTON_TEXT') ||
      getEnv('REACT_APP_PUBLIC_OIDC_LOGIN_BUTTON_TEXT') ||
      'Continue with Blocks',
  };
};

export const OIDC_STATE_STORAGE_KEY = 'oidc-login-state';

export const persistOidcLoginState = (state: string, goto?: string | null) => {
  sessionStorage.setItem(
    OIDC_STATE_STORAGE_KEY,
    JSON.stringify({
      state,
      goto: goto || null,
    })
  );
};

export const consumeOidcLoginState = () => {
  const raw = sessionStorage.getItem(OIDC_STATE_STORAGE_KEY);
  sessionStorage.removeItem(OIDC_STATE_STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as { state: string; goto: string | null };
  } catch (_error) {
    return null;
  }
};

export const buildOidcAuthorizeUrl = ({
  initiateUrl,
  clientId,
  redirectUri,
  blocksKey,
}: {
  initiateUrl: string;
  clientId: string;
  redirectUri: string;
  blocksKey: string;
}) => {
  const url = new URL(initiateUrl);
  url.searchParams.set('x-blocks-key', blocksKey);
  url.searchParams.set('clientId', clientId);
  url.searchParams.set('redirectUri', redirectUri);
  return url.toString();
};
